import type { ChapterContextEntry, EpubToc } from '../../types';

const MAX_DEPTH = 3;

/**
 * Given a highlight's global text offset and the document's TOC (with offsets),
 * return the chapter breadcrumb — the chain of TOC ancestors containing this highlight.
 */
export function resolveChapterBreadcrumb(
  highlightOffset: number,
  tocItems: EpubToc[],
): ChapterContextEntry[] {
  // Filter to items with offsets, capped at MAX_DEPTH, sorted by offset
  const itemsWithOffsets = tocItems
    .filter((item): item is EpubToc & { offset: number; } =>
      item.offset != null && item.level <= MAX_DEPTH)
    .sort((a, b) => a.offset - b.offset);

  if (itemsWithOffsets.length === 0) {
    return [];
  }

  // For each level (1-3), find the last TOC entry whose offset <= highlightOffset.
  // When a shallower level updates, clear deeper levels (they no longer apply).
  const bestAtLevel: { [key: number]: typeof itemsWithOffsets[number] | undefined; } = {};

  for (const item of itemsWithOffsets) {
    if (item.offset > highlightOffset) {
      break;
    }
    bestAtLevel[item.level] = item;
    // Clear all deeper levels — they belonged to the previous section
    for (let deeper = item.level + 1; deeper <= MAX_DEPTH; deeper++) {
      bestAtLevel[deeper] = undefined;
    }
  }

  // Build breadcrumb from shallowest to deepest
  const breadcrumb: ChapterContextEntry[] = [];
  for (let level = 1; level <= MAX_DEPTH; level++) {
    const item = bestAtLevel[level];
    if (!item) {
      continue;
    }
    breadcrumb.push({
      title: item.title,
      level: item.level,
      toc_id: item.id,
      offset: item.offset,
    });
  }

  return breadcrumb;
}
